import { useRef, useState, useEffect } from 'react';

interface CategoryTag {
  id: string;
  label: string;
  isActive?: boolean;
}

interface CategoryTagScrollerProps {
  tags: CategoryTag[];
  onTagClick?: (tagId: string) => void;
}

export function CategoryTagScroller({ tags, onTagClick }: CategoryTagScrollerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  return (
    <div className="mt-4 -mx-4 md:mt-6 md:mx-0">
      <div
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        className={`flex gap-3 overflow-x-auto px-4 pb-0 md:px-0 md:pb-2 [&::-webkit-scrollbar]:hidden ${
          isDragging ? 'cursor-grabbing' : 'cursor-grab'
        }`}
      >
        {tags.map((tag) => (
          <button
            key={tag.id}
            onClick={() => onTagClick?.(tag.id)}
            className={`shrink-0 rounded-sm px-6 py-3 text-sm font-semibold transition-colors ${
              tag.isActive
                ? 'bg-[#476d5e] text-white hover:bg-[#3d5f52]'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {tag.label}
          </button>
        ))}
      </div>
    </div>
  );
}
